import { useState, useCallback } from 'react';
import { Menu } from '@headlessui/react';
import {
  TrashIcon,
  EllipsisVerticalIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

export default function ImageBlockWidget({ imageBlock, onDelete, readOnly = false }) {
  const { hasPermission } = useAuthStore();
  const canEdit = !readOnly && hasPermission('edit_dashboards');

  const [imgError, setImgError] = useState(false);

  const stopPropagation = (e) => e.stopPropagation();

  const handleMenuClick = (e, callback) => {
    e.stopPropagation();
    e.preventDefault();
    callback?.();
  };

  const handleDelete = useCallback(async () => {
    if (!window.confirm('Delete this image block?')) return;
    try {
      await apiHelpers.deleteImageBlock(imageBlock._id);
      onDelete?.(imageBlock._id);
      toast.success('Image block deleted');
    } catch {
      toast.error('Failed to delete image block');
    }
  }, [imageBlock?._id, onDelete]);

  const objectFitClass = {
    contain: 'object-contain',
    cover: 'object-cover',
    fill: 'object-fill'
  }[imageBlock?.objectFit] || 'object-contain';

  return (
    <div className="flex flex-col h-full">
      {/* Header / Drag Handle */}
      <div className="drag-handle flex items-center justify-between px-4 py-2 border-b border-surface-200 dark:border-surface-700 cursor-grab active:cursor-grabbing bg-surface-50/50 dark:bg-surface-800/50">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <PhotoIcon className="w-4 h-4 text-primary-500 flex-shrink-0" />
          <span className="text-sm font-medium text-surface-700 dark:text-surface-300 truncate">
            {imageBlock?.title || imageBlock?.originalFilename || 'Image'}
          </span>
        </div>

        {canEdit && (
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            <Menu as="div" className="relative" onClick={stopPropagation} onTouchStart={stopPropagation} onTouchEnd={stopPropagation}>
              <Menu.Button
                className="p-1 rounded hover:bg-surface-200 dark:hover:bg-surface-700"
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
                onTouchStart={stopPropagation}
                onTouchEnd={stopPropagation}
              >
                <EllipsisVerticalIcon className="w-4 h-4 text-surface-500" />
              </Menu.Button>
              <Menu.Items className="absolute right-0 z-50 mt-1 w-40 bg-white dark:bg-surface-800 rounded-lg shadow-lg border border-surface-200 dark:border-surface-700 py-1">
                <button
                  type="button"
                  onClick={(e) => handleMenuClick(e, handleDelete)}
                  onMouseDown={stopPropagation}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-left"
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete
                </button>
              </Menu.Items>
            </Menu>
          </div>
        )}
      </div>

      {/* Image Body */}
      <div className="flex-1 overflow-hidden flex items-center justify-center p-2 bg-surface-50 dark:bg-surface-800/30">
        {imgError ? (
          <div className="flex flex-col items-center justify-center gap-2 text-surface-400">
            <PhotoIcon className="w-10 h-10" />
            <span className="text-sm">Failed to load image</span>
          </div>
        ) : (
          <img
            src={imageBlock?.imageUrl}
            alt={imageBlock?.altText || 'Dashboard image'}
            className={`w-full h-full ${objectFitClass} rounded`}
            onError={() => setImgError(true)}
            draggable={false}
          />
        )}
      </div>
    </div>
  );
}
